import React, { useRef, useState } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * TeamBulkUpload
 * --------------
 * Counterpart to `EneaflixBulkUpload` but for team-member photos. The
 * server-side matching is FUZZY (token subset): a filename like
 * `heloisa-labres.jpg` matches the member "Heloisa da Silva Labres"
 * because its tokens are a subset of the member's name tokens.
 *
 * Generic-only names (e.g. `heloisa.jpg`) are refused as ambiguous so
 * the admin gets a clear error instead of a wrong association.
 */
const TeamBulkUpload = ({ onComplete }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(
        `${API_URL}/admin/team/bulk-photo-upload`,
        fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setReport(res.data);
      const s = res.data.summary || {};
      toast.success(
        `Upload concluído: ${s.matched || 0}/${s.received || 0} associadas` +
          (s.unmatched ? ` · ${s.unmatched} sem match` : '') +
          (s.errors ? ` · ${s.errors} erros` : '')
      );
      if (onComplete) onComplete(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no upload em massa');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="mb-8" data-testid="team-bulk-upload">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-2xl p-8 transition-colors text-center ${
          dragOver
            ? 'border-accent-orange bg-orange-50'
            : 'border-neutral-300 bg-neutral-50'
        }`}
      >
        <iconify-icon
          icon="solar:users-group-rounded-linear"
          className="text-5xl text-accent-orange mb-3"
        ></iconify-icon>
        <h3 className="font-semibold text-lg mb-1">Upload em lote de fotos</h3>
        <p className="text-sm text-neutral-600 mb-4 max-w-xl mx-auto">
          Arraste várias fotos aqui ou clique para selecionar. O sistema casa o
          arquivo pelo nome (precisa pelo menos nome + sobrenome).<br />
          Ex.: <code>heloisa-labres.jpg</code> → <strong>Heloisa da Silva Labres</strong>.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="team-bulk-file-input"
        />
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="team-bulk-pick-btn"
        >
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <iconify-icon icon="solar:refresh-linear" className="animate-spin"></iconify-icon>
              Enviando…
            </span>
          ) : (
            'Selecionar fotos'
          )}
        </Button>
      </div>

      {report && (
        <div
          className="mt-4 bg-white rounded-xl border border-neutral-200 divide-y"
          data-testid="team-bulk-report"
        >
          <div className="p-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="font-semibold">Resultado:</span>
            <span className="text-emerald-600">
              ✓ {report.summary.matched} associadas
            </span>
            {report.summary.unmatched > 0 && (
              <span className="text-amber-600">
                ⚠ {report.summary.unmatched} sem match
              </span>
            )}
            {report.summary.errors > 0 && (
              <span className="text-red-600">
                ✗ {report.summary.errors} erros
              </span>
            )}
          </div>

          {report.matched && report.matched.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-2">
                Associadas
              </h4>
              <ul className="space-y-1 text-sm">
                {report.matched.map((m) => (
                  <li key={m.member_id} className="flex items-center gap-3">
                    <img
                      src={m.url}
                      alt=""
                      className="w-10 h-10 object-cover rounded-full border border-neutral-200"
                    />
                    <div>
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-neutral-500">{m.file}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.unmatched && report.unmatched.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-2">
                Sem match — renomeie e tente novamente
              </h4>
              <ul className="space-y-1 text-sm text-neutral-700">
                {report.unmatched.map((u) => (
                  <li key={u.file} className="flex justify-between items-center">
                    <span>{u.file}</span>
                    <code className="text-xs text-neutral-500">
                      tokens: [{(u.tokens || []).join(', ')}]
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.errors && report.errors.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-red-700 mb-2">
                Erros
              </h4>
              <ul className="space-y-1 text-sm text-red-700">
                {report.errors.map((er) => (
                  <li key={er.file}>
                    <span className="font-medium">{er.file}</span> — {er.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamBulkUpload;
